import React from 'react'
import PropTypes, { any } from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { DataGrid } from '@mui/x-data-grid'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import StatusChip from '../../../../../components/genericcomponents/StatusChip/status-chip'
import LinkButton from '../../../../../components/LinkButton/link-button'
import { setSelectedShipment } from '../../order-list-slice'
import { useTheme } from '../../../../../components/mui/Styles/index'
import Constants from '../../../../../constants/en.json'
import {
  getCurrencyFormattedValue,
  getDateTimeFormattedValue,
} from '../../../../../utils/string-utils'
import NoResultsFoundTableOverlay from '../../../../../components/NoResultsFoundTableOverlay/no-results-found-table-overlay'

export function ShipmentTable({ filterBy, filterValue, searchByOptions }) {
  const theme = useTheme()
  const shipments = useSelector((state) => state.orderList.shipments)
  const isFetchingShipments = useSelector((state) => state.orderList.isFetchingShipments)
  const order = useSelector((state) => state.order.selectedOrder)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const selectedSearchOption = searchByOptions.filter((option) => {
    return option.id === filterBy
  })
  const filterByDescription = selectedSearchOption[0]?.text || ''
  const filterByType = selectedSearchOption[0]?.type
  const noResultsValue = filterByType === 'date' ? moment.utc(filterValue).format('L') : filterValue
  const rows = shipments?.map((row) => ({
    id: row.wmsOrderNum,
    wmsOrderNum: row.wmsOrderNum,
    lines: row.numLine,
    createdOn: row.createdOn,
    orderStatus: row.orderStatus,
    shipmentValue: row.shipmentValue,
    dateShipped: row.dateShipped,
  }))

  const columns = [
    {
      field: 'wmsOrderNum',
      headerName: Constants.orders.SHIPMENT,
      renderCell: (cellValues) => {
        const wmsOrderNumber = cellValues.row.wmsOrderNum
        return (
          <LinkButton
            handleLinkClick={() => {
              handleSelectShipment(cellValues.row)
            }}
            linkText={wmsOrderNumber}
          />
        )
      },
      flex: 0.3,
      minWidth: 150,
    },
    {
      field: 'lines',
      headerName: Constants.orders.LINES,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: (value) => value || 'Pending',
      flex: 0.2,
      minWidth: 100,
    },
    {
      field: 'createdOn',
      minWidth: 200,
      headerName: Constants.orders.CREATED_ON,
      headerAlign: 'right',
      align: 'right',
      renderCell: (cellValues) => {
        return getDateTimeFormattedValue(cellValues.value)
      },
      flex: 0.2,
    },
    {
      field: 'orderStatus',
      headerName: Constants.orders.STATUS,
      renderCell: (cellValues) => {
        if (!cellValues.row.orderStatus) {
          return 'Pending'
        }
        return <StatusChip status={cellValues.row.orderStatus} />
      },
      flex: 0.2,
      minWidth: 180,
    },
    {
      field: 'shipmentValue',
      headerName: Constants.orders.SHIPMENT_VALUE,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: (value) => (value ? getCurrencyFormattedValue(value) : 'Pending'),
      sortComparator: (a, b) => a - b,
      flex: 0.2,
      minWidth: 180,
    },
    {
      field: 'dateShipped',
      headerName: Constants.orders.DATE_SHIPPED,
      headerAlign: 'right',
      align: 'right',
      renderCell: (cellValues) => {
        return getDateTimeFormattedValue(cellValues.value) || 'Pending'
      },
      flex: 0.3,
      minWidth: 200,
    },
  ]

  function handleSelectShipment(shipment) {
    dispatch(setSelectedShipment(shipment))
    navigate(
      `/orderdetails?wmsOrderId=${shipment.wmsOrderNum}&dmsOrderId=${order.dms_order}&status=${shipment.orderStatus}`,
    )
  }

  const gridStyle = {
    minHeight: '160px',
    height: isFetchingShipments ? '400px' : 'auto',
    backgroundColor: theme.palette.common.white,
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: theme.palette.grey[300],
  }

  const filterModel = {
    items: [{ field: filterBy, operator: 'contains', value: filterValue }],
  }

  return (
    <DataGrid
      sx={gridStyle}
      rows={rows}
      columns={columns}
      loading={isFetchingShipments}
      filterModel={filterModel}
      spacing={1}
      pageSizeOptions={[25, 50]}
      initialState={{
        pagination: {
          paginationModel: { pageSize: 25, page: 0 },
        },
      }}
      slotProps={{
        pagination: {
          component: 'div',
          showLastButton: true,
          showFirstButton: true,
        },
        noResultsOverlay: {
          filterByDescription,
          noResultsValue,
        },
        loadingOverlay: {
          variant: 'skeleton',
          noRowsVariant: 'skeleton',
        },
      }}
      slots={{
        noResultsOverlay: NoResultsFoundTableOverlay,
      }}
      disableColumnFilter
      disableRowSelectionOnClick
    />
  )
}

ShipmentTable.propTypes = {
  filterBy: PropTypes.string.isRequired,
  filterValue: PropTypes.string.isRequired,
  searchByOptions: PropTypes.arrayOf(PropTypes.objectOf(any)).isRequired,
}

export default ShipmentTable
